import React, { useEffect, useState } from "react";
import { Navigate, Route, Routes, Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2, LogOut, LayoutDashboard, Users, FileText, CalendarClock, Layers, Briefcase, Rocket, BookOpen, Award, Image as ImageIcon, Home as HomeIcon, Calculator, Target, Handshake, PanelTop } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { api, setAdminToken, getAdminToken, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/common/Logo";
import AdminContent from "@/pages/admin/AdminContent";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminHomeSettings from "@/pages/admin/AdminHomeSettings";
import AdminPageContent from "@/pages/admin/AdminPageContent";

function useAdminGuard() {
    const [state, setState] = useState({ loading: true, ok: false });
    useEffect(() => {
        if (!getAdminToken()) { setState({ loading: false, ok: false }); return; }
        api.get("/auth/me").then(() => setState({ loading: false, ok: true })).catch(() => { setAdminToken(null); setState({ loading: false, ok: false }); });
    }, []);
    return state;
}

function AdminLogin() {
    useSEO({ title: "Admin Login | RKS Systems" });
    const nav = useNavigate();
    const [form, setForm] = useState({ email: "admin@rkssystems.com", password: "" });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const { data } = await api.post("/auth/login", form);
            setAdminToken(data.access_token);
            nav("/admin/dashboard");
        } catch (e) {
            setError(formatApiErrorDetail(e?.response?.data?.detail) || "Invalid credentials");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[hsl(222_47%_7%)] text-white noise" data-testid="page-admin-login">
            <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />
            <div className="relative container-app min-h-screen flex items-center justify-center">
                <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur-xl">
                    <Logo variant="light" />
                    <h1 className="mt-8 font-heading text-2xl font-bold">Admin Sign In</h1>
                    <p className="mt-2 text-sm text-white/60">Restricted area. Authorized personnel only.</p>
                    <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-login-form">
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-widest text-white/60">Email</label>
                            <input required type="email" data-testid="admin-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 w-full rounded-md bg-[hsl(222_47%_10%)] border border-white/15 px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary" />
                        </div>
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-widest text-white/60">Password</label>
                            <input required type="password" data-testid="admin-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 w-full rounded-md bg-[hsl(222_47%_10%)] border border-white/15 px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary" />
                        </div>
                        {error && <div className="rounded-md border border-red-400/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-200">{error}</div>}
                        <button type="submit" data-testid="admin-login-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-3 font-semibold text-white hover:bg-primary/90 btn-lift disabled:opacity-50">
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                            {loading ? "Signing in..." : "Sign in"}
                        </button>
                    </form>
                    <Link to="/" className="mt-6 block text-center text-sm text-white/50 hover:text-white">← Back to site</Link>
                </div>
            </div>
        </div>
    );
}

function AdminShell({ children }) {
    const nav = useNavigate();
    const loc = useLocation();
    const logout = () => { setAdminToken(null); nav("/admin/login"); };
    const inboundLinks = [
        { to: "/admin/dashboard", icon: LayoutDashboard, label: "Dashboard" },
        { to: "/admin/leads", icon: Users, label: "Leads" },
        { to: "/admin/quotes", icon: FileText, label: "Quotes" },
        { to: "/admin/meetings", icon: CalendarClock, label: "Meetings" },
    ];
    const contentLinks = [
        { to: "/admin/settings/home", icon: HomeIcon, label: "Home Page" },
        { to: "/admin/page-banners", icon: PanelTop, label: "Page Banners" },
        { to: "/admin/content/services", icon: Briefcase, label: "Services" },
        { to: "/admin/content/industries", icon: Layers, label: "Industries" },
        { to: "/admin/content/engagement-models", icon: Handshake, label: "Engagement Models" },
        { to: "/admin/content/products", icon: Rocket, label: "Products" },
        { to: "/admin/content/success-stories", icon: Award, label: "Success Stories" },
        { to: "/admin/content/blogs", icon: BookOpen, label: "Blogs" },
        { to: "/admin/media", icon: ImageIcon, label: "Media Library" },
    ];
    const costLinks = [
        { to: "/admin/content/cost-roles", icon: Calculator, label: "Cost Roles" },
        { to: "/admin/content/cost-challenges", icon: Target, label: "Engineering Challenges" },
    ];
    const isActive = (to) => loc.pathname === to || (to.includes("/content/") && loc.pathname.startsWith(to));
    return (
        <div className="min-h-screen bg-secondary/40" data-testid="admin-shell">
            <div className="grid grid-cols-[240px,1fr] min-h-screen">
                <aside className="border-r border-border bg-white flex flex-col">
                    <div className="px-6 py-6 border-b border-border">
                        <Logo />
                    </div>
                    <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
                        <div className="px-3 pb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Inbound</div>
                        {inboundLinks.map((l) => {
                            const active = isActive(l.to);
                            const Icon = l.icon;
                            return (
                                <Link key={l.to} to={l.to} data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${active ? "bg-primary/10 text-primary" : "text-foreground/70 hover:bg-secondary hover:text-foreground"}`}>
                                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                                    {l.label}
                                </Link>
                            );
                        })}
                        <div className="mt-6 px-3 pb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Content</div>
                        {contentLinks.map((l) => {
                            const active = isActive(l.to);
                            const Icon = l.icon;
                            return (
                                <Link key={l.to} to={l.to} data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${active ? "bg-primary/10 text-primary" : "text-foreground/70 hover:bg-secondary hover:text-foreground"}`}>
                                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                                    {l.label}
                                </Link>
                            );
                        })}
                        <div className="mt-6 px-3 pb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Cost Explorer</div>
                        {costLinks.map((l) => {
                            const active = isActive(l.to);
                            const Icon = l.icon;
                            return (
                                <Link key={l.to} to={l.to} data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${active ? "bg-primary/10 text-primary" : "text-foreground/70 hover:bg-secondary hover:text-foreground"}`}>
                                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                                    {l.label}
                                </Link>
                            );
                        })}
                    </nav>
                    <div className="p-4 border-t border-border">
                        <button onClick={logout} data-testid="admin-logout" className="w-full inline-flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium text-foreground/70 hover:bg-secondary hover:text-foreground">
                            <LogOut className="h-4 w-4" strokeWidth={1.75} /> Logout
                        </button>
                    </div>
                </aside>
                <div className="min-w-0">
                    {children}
                </div>
            </div>
        </div>
    );
}

function AdminDashboard() {
    useSEO({ title: "Dashboard — Admin | RKS Systems" });
    const [stats, setStats] = useState(null);
    useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setStats(data)); }, []);
    const cards = [
        { key: "total_leads", label: "Total Leads" },
        { key: "total_quotes", label: "Quote Requests" },
        { key: "total_meetings", label: "Meeting Requests" },
        { key: "new_leads_last_7_days", label: "New Leads (7d)" },
    ];
    return (
        <div className="p-8" data-testid="page-admin-dashboard">
            <h1 className="font-heading text-3xl font-bold">Dashboard</h1>
            <p className="text-muted-foreground mt-1 text-sm">Snapshot of lead capture and inbound activity.</p>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                {cards.map((c) => (
                    <div key={c.key} data-testid={`admin-stat-${c.key}`} className="rounded-xl border border-border bg-white p-6">
                        <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{c.label}</div>
                        <div className="mt-2 font-heading text-3xl font-extrabold">{stats?.[c.key] ?? "—"}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function AdminTable({ endpoint, columns, title, testid }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => { api.get(endpoint).then(({ data }) => setItems(data.items || [])).finally(() => setLoading(false)); }, [endpoint]);
    return (
        <div className="p-8" data-testid={testid}>
            <h1 className="font-heading text-3xl font-bold">{title}</h1>
            <div className="mt-8 rounded-xl border border-border bg-white overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-secondary/40 border-b border-border">
                        <tr>
                            {columns.map((c) => (
                                <th key={c.key} className="text-left px-4 py-3 font-heading font-semibold text-xs uppercase tracking-wider text-muted-foreground">{c.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-muted-foreground">No records yet.</td></tr>
                        ) : items.map((row) => (
                            <tr key={row.id} className="border-t border-border hover:bg-secondary/30">
                                {columns.map((c) => (
                                    <td key={c.key} className="px-4 py-3 align-top">{c.render ? c.render(row) : (row[c.key] ?? "—")}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function AdminLeads() {
    useSEO({ title: "Leads — Admin | RKS Systems" });
    return (
        <AdminTable
            endpoint="/admin/leads"
            title="Leads"
            testid="page-admin-leads"
            columns={[
                { key: "created_at", label: "When", render: (r) => new Date(r.created_at).toLocaleString() },
                { key: "name", label: "Name" },
                { key: "email", label: "Email" },
                { key: "company", label: "Company" },
                { key: "message", label: "Message", render: (r) => <span className="line-clamp-2 block max-w-md">{r.message}</span> },
            ]}
        />
    );
}

function AdminQuotes() {
    useSEO({ title: "Quotes — Admin | RKS Systems" });
    return (
        <AdminTable
            endpoint="/admin/quotes"
            title="Quote Requests"
            testid="page-admin-quotes"
            columns={[
                { key: "created_at", label: "When", render: (r) => new Date(r.created_at).toLocaleString() },
                { key: "name", label: "Name" },
                { key: "company", label: "Company" },
                { key: "email", label: "Email" },
                { key: "service", label: "Service" },
                { key: "engagement_model", label: "Model" },
                { key: "requirement", label: "Requirement", render: (r) => <span className="line-clamp-2 block max-w-md">{r.requirement}</span> },
            ]}
        />
    );
}

function AdminMeetings() {
    useSEO({ title: "Meetings — Admin | RKS Systems" });
    return (
        <AdminTable
            endpoint="/admin/meetings"
            title="Meeting Requests"
            testid="page-admin-meetings"
            columns={[
                { key: "created_at", label: "Submitted", render: (r) => new Date(r.created_at).toLocaleString() },
                { key: "name", label: "Name" },
                { key: "company", label: "Company" },
                { key: "email", label: "Email" },
                { key: "preferred_date", label: "Preferred date" },
                { key: "preferred_time", label: "Time" },
                { key: "summary", label: "Summary", render: (r) => <span className="line-clamp-2 block max-w-md">{r.summary}</span> },
            ]}
        />
    );
}

function Protected({ children }) {
    const { loading, ok } = useAdminGuard();
    if (loading) return <div className="min-h-screen flex items-center justify-center bg-secondary/40"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
    if (!ok) return <Navigate to="/admin/login" replace />;
    return <AdminShell>{children}</AdminShell>;
}

export default function AdminRouter() {
    return (
        <Routes>
            <Route path="login" element={<AdminLogin />} />
            <Route path="dashboard" element={<Protected><AdminDashboard /></Protected>} />
            <Route path="leads" element={<Protected><AdminLeads /></Protected>} />
            <Route path="quotes" element={<Protected><AdminQuotes /></Protected>} />
            <Route path="meetings" element={<Protected><AdminMeetings /></Protected>} />
            <Route path="content/:kind" element={<Protected><AdminContent /></Protected>} />
            <Route path="media" element={<Protected><AdminMedia /></Protected>} />
            <Route path="settings/home" element={<Protected><AdminHomeSettings /></Protected>} />
            <Route path="page-banners" element={<Protected><AdminPageContent /></Protected>} />
            <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
        </Routes>
    );
}
